import React from 'react';
import { BalanceReturn } from '../clients/rust-api-client';
import { Text } from '../Components/Catalyst/text';
import { formatBillingDate, formatMoney } from '../utils/billingHelpers';
import { accountAmountClasses, accountState } from './accountState';

interface Props {
  balance: BalanceReturn | null;
  /** Where the due date comes from; the balance does not carry one. */
  dueDate?: string | null;
}

/**
 * The one number a homeowner opens the portal to see.
 *
 * **Deliberately one figure and one sentence.** front-porch's
 * `HomeBalanceCard` shows the balance, the current period charge, the
 * oldest unpaid due date and all five aging buckets, which is right for
 * someone working delinquency across an association. Shown to the
 * person who owes it, that is a spreadsheet where a sentence would do —
 * so the buckets are gone and "47 days past due" carries the same
 * information a homeowner can act on.
 *
 * All the wording comes from {@link accountState}, which is where the
 * traps live: a credit never renders with a minus sign, and a home with
 * no schedule in force never reads as "$0.00, all paid up".
 */
const BalanceHero: React.FC<Props> = ({ balance, dueDate }) => {
  const state = accountState(balance);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-gray-800 sm:p-8">
      <div className="text-sm text-zinc-500 dark:text-zinc-400">
        {state.owes ? 'Balance due' : 'Your balance'}
      </div>

      <div
        className={`mt-1 text-4xl font-semibold tabular-nums sm:text-5xl ${accountAmountClasses(
          state.status
        )}`}
      >
        {formatMoney(state.amount)}
      </div>

      <div className="mt-2 text-base text-zinc-700 dark:text-zinc-300">
        {state.status === 'credit' ? 'Credit on your account' : state.headline}
      </div>

      {state.status === 'credit' && (
        <Text className="mt-2 text-sm">
          You have paid more than you owe. This will go towards what you are
          charged next.
        </Text>
      )}

      {state.status === 'unconfigured' && (
        <Text className="mt-2 text-sm">
          Your association has not set up recurring dues for this home yet. If
          you think that is wrong, contact your association manager.
        </Text>
      )}

      {state.owes && dueDate && (
        <div className="mt-4 text-sm text-zinc-600 dark:text-zinc-400">
          {state.status === 'past_due' ? 'Was due ' : 'Due '}
          <span className="font-medium text-zinc-900 dark:text-white">
            {formatBillingDate(dueDate)}
          </span>
        </div>
      )}

      {balance && (
        <div className="mt-4 text-xs text-zinc-400 dark:text-zinc-400">
          As of {new Date(balance.updated_at * 1000).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default BalanceHero;
